import WebAppAPI from '@/utils/WebAppAPI';

export interface JiraIntegrationStatus {
  hasJiraInstalled: boolean;
  jira_site_url: string | null;
  jira_admin_email: string | null;
  jira_connected_at: string | null;
  jira_auto_sync?: boolean;
  auto_sync_jira_updates?: boolean;
}

export interface JiraConnectPayload {
  jira_admin_email: string;
  jira_api_key: string;
  jira_url: string;
  auto_sync_jira_updates?: boolean;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
  self?: string;
  projectTypeKey?: string;
}

export interface JiraProjectsResponse {
  success?: boolean;
  data?: JiraProject[];
  meta?: JiraProjectsMeta;
  page?: number;
  limit?: number;
  startAt?: number;
  maxResults?: number;
  total: number;
  isLast: boolean;
  values: JiraProject[];
}

export interface JiraProjectsMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface JiraEpic {
  key: string;
  name: string;
}

export interface JiraProjectEpicsResponse {
  success?: boolean;
  data?: JiraEpic[];
  meta?: JiraProjectsMeta;
  values: JiraEpic[];
  page?: number;
  limit?: number;
  total?: number;
  isLast?: boolean;
  next_cursor?: string | null;
  is_last?: boolean;
}

export interface JiraImportPreview {
  total_jira_tasks: number;
  total_jira_sub_tasks: number;
  total_crafter_tasks: number;
  project_prefill: {
    name: string;
    description: string;
  };
}

export interface JiraCreateProjectPayload {
  name: string;
  description: string;
  priority: string;
  start_date: string;
  end_date: string;
  jira_project_key: string;
  jira_epic_key: string;
  files: File[];
  slack_channel?: string;
  status?: string;
  model_type?: 'openai' | 'gemini';
  reference_links?: any[];
}

export interface JiraAutoSyncPayload {
  enabled: boolean;
}

const JiraService = {
  getIntegrationStatus: async (): Promise<JiraIntegrationStatus> => {
    const { data } = await WebAppAPI().get(
      '/organizations/jira-integration-status'
    );
    const status = data?.data ?? data;
    return {
      ...status,
      auto_sync_jira_updates:
        status?.auto_sync_jira_updates ?? status?.jira_auto_sync,
    };
  },

  connectOrUpdate: async (
    organizationId: string,
    payload: JiraConnectPayload
  ): Promise<JiraIntegrationStatus> => {
    const { data } = await WebAppAPI().put(
      `/organizations/${organizationId}/jira-integration`,
      payload
    );
    const status =
      data?.data?.hasJiraInstalled !== undefined ? data.data : data;
    return {
      hasJiraInstalled: Boolean(status?.hasJiraInstalled),
      jira_site_url: status?.jira_site_url ?? payload.jira_url,
      jira_admin_email: status?.jira_admin_email ?? payload.jira_admin_email,
      jira_connected_at: status?.jira_connected_at ?? null,
      auto_sync_jira_updates:
        status?.auto_sync_jira_updates ??
        status?.jira_auto_sync ??
        payload.auto_sync_jira_updates,
    };
  },

  disconnect: async (organizationId: string): Promise<any> => {
    const { data } = await WebAppAPI().delete(
      `/organizations/${organizationId}/jira-integration`
    );
    return data;
  },

  updateAutoSync: async (
    organizationId: string,
    payload: JiraAutoSyncPayload
  ): Promise<Partial<JiraIntegrationStatus>> => {
    const { data } = await WebAppAPI().put(
      `/organizations/${organizationId}/jira-auto-sync`,
      payload
    );
    const status = data?.data ?? data;
    return {
      ...status,
      auto_sync_jira_updates:
        status?.auto_sync_jira_updates ??
        status?.jira_auto_sync ??
        status?.enabled ??
        payload.enabled,
    };
  },

  listProjects: async (
    organizationId: string,
    params: { page?: number; limit?: number; search?: string } = {}
  ): Promise<JiraProjectsResponse> => {
    const { data } = await WebAppAPI().get(
      `/organizations/${organizationId}/jira-projects`,
      {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.search ? { search: params.search } : {}),
        },
      }
    );
    const projects = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.values)
        ? data.values
        : [];
    const meta = data?.meta ?? {};
    const total = meta.total ?? data?.total ?? projects.length;
    const page = meta.page ?? data?.page ?? params.page ?? 1;
    const limit = meta.limit ?? data?.limit ?? params.limit ?? 10;
    const pages =
      meta.pages ??
      data?.pages ??
      data?.totalPages ??
      Math.max(1, Math.ceil(total / limit));

    return {
      ...data,
      data: projects,
      values: projects,
      meta: {
        total,
        page,
        limit,
        pages,
      },
      total,
      page,
      limit,
      isLast: page >= pages,
    };
  },

  listProjectEpics: async (
    organizationId: string,
    projectKey: string,
    params: { page?: number; limit?: number; search?: string } = {}
  ): Promise<JiraProjectEpicsResponse> => {
    const { data } = await WebAppAPI().get(
      `/organizations/${organizationId}/jira-import/projects/${projectKey}/epics`,
      {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.search ? { search: params.search } : {}),
        },
      }
    );
    const epics = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.values)
        ? data.values
        : [];
    const meta = data?.meta ?? {};
    const total = meta.total ?? data?.total ?? epics.length;
    const page = meta.page ?? data?.page ?? params.page ?? 1;
    const limit = meta.limit ?? data?.limit ?? params.limit ?? 10;
    const pages =
      meta.pages ??
      data?.pages ??
      data?.totalPages ??
      Math.max(1, Math.ceil(total / limit));

    return {
      ...data,
      data: epics,
      values: epics,
      meta: {
        total,
        page,
        limit,
        pages,
      },
      total,
      page,
      limit,
      isLast: page >= pages,
      is_last: page >= pages,
    };
  },

  previewEpicImport: async (
    organizationId: string,
    projectKey: string,
    epicKey: string
  ): Promise<JiraImportPreview> => {
    const { data } = await WebAppAPI().get(
      `/organizations/${organizationId}/jira-import/projects/${projectKey}/epics/${epicKey}/preview`
    );
    return data?.data;
  },

  createProjectFromJira: async (
    payload: JiraCreateProjectPayload
  ): Promise<any> => {
    const formData = new FormData();

    formData.append('name', payload.name);
    formData.append('description', payload.description);
    formData.append('priority', payload.priority);
    formData.append('start_date', payload.start_date);
    formData.append('end_date', payload.end_date);
    formData.append('jira_project_key', payload.jira_project_key);
    formData.append('jira_epic_key', payload.jira_epic_key);
    formData.append('model_type', payload.model_type ?? 'openai');
    formData.append(
      'reference_links',
      JSON.stringify(payload.reference_links ?? [])
    );

    if (payload.slack_channel) {
      formData.append('slack_channel', payload.slack_channel);
    }

    if (payload.status) {
      formData.append('status', payload.status);
    }

    payload.files.forEach((file) => {
      formData.append('files', file);
    });

    const { data } = await WebAppAPI().post(
      '/projects/create-project-from-jira',
      formData
    );
    return data;
  },
};

export default JiraService;
